import { useTranslation } from 'react-i18next';
import {
	HeadContent,
	Outlet,
	Scripts,
	createRootRouteWithContext,
	useRouter,
} from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { Suspense, lazy, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';

import { t } from '@/lib/i18n';
import appCss from '@/assets/styles.css?url';
import Header from '@/components/layout/header';
import Footer from '@/components/layout/footer';
import NotFound from '@/components/layout/not-found.tsx';
import Devtools from '@/components/integrations/tanstack/devtools.tsx';
import GDPRBanner from '@/components/integrations/google/gdpr-banner.tsx';
import Cart from '@/components/features/cart';
import ServiceDateModal from '@/components/features/service-date-modal';
import { getMainInfo } from '@/lib/server-fns.ts';
import cartStore from '@/stores/cart.store.ts';
import Messengers from '@/components/partials/messengers';

const { VITE_LOCALE, VITE_GTM, VITE_GA_MEASUREMENT_ID } = import.meta.env;

const Snowfall = lazy(() => import('react-snowfall'));

export const Route = createRootRouteWithContext()({
	loader: () => getMainInfo(),
	head: ({ loaderData }) => ({
		meta: [
			{
				charSet: 'utf-8',
			},
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1, maximum-scale=1',
			},
			{
				name: 'apple-mobile-web-app-title',
				content: `Jervice - ${t('global.menu.home')}`,
			},
			{
				name: 'og:title',
				content: `Jervice - ${t('global.menu.home')}`,
			},
			{
				name: 'og:description',
				content: t('hero.description'),
			},
			{
				name: 'og:url',
				content: `https://jervice.${VITE_LOCALE === 'en' ? 'eu' : 'pl'}`,
			},
			{
				name: 'og:image',
				content: loaderData?.playerPhotos?.at(1)?.url || '/images/main.png',
			},
			{
				name: 'twitter:title',
				content: `Jervice - ${t('global.menu.home')}`,
			},
			{
				name: 'twitter:description',
				content: t('hero.description'),
			},
			{
				name: 'twitter:url',
				content: `https://jervice.${VITE_LOCALE === 'en' ? 'eu' : 'pl'}`,
			},
			{
				name: 'twitter:image',
				content: loaderData?.playerPhotos?.at(1)?.url || '/images/main.png',
			},
			{ title: `Jervice - ${t('global.menu.home')}` },
		],
		links: [
			{
				rel: 'icon',
				type: 'image/png',
				href: '/favicon-96x96.png',
				sizes: '96x96',
			},
			{
				rel: 'icon',
				type: 'image/svg+xml',
				href: '/favicon.svg',
			},
			{
				rel: 'shortcut icon',
				href: '/favicon.ico',
			},
			{
				rel: 'apple-touch-icon',
				href: '/apple-touch-icon.png',
				sizes: '180x180',
			},
			{
				rel: 'manifest',
				href: '/site.webmanifest',
			},
			{
				rel: 'stylesheet',
				href: appCss,
			},
			{
				rel: 'preconnect',
				href: 'https://fonts.googleapis.com',
			},
			{
				rel: 'preconnect',
				href: 'https://fonts.gstatic.com',
				crossOrigin: 'anonymous',
			},
			{
				rel: 'stylesheet',
				href: 'https://fonts.googleapis.com/css2?family=Goldman:wght@400;700&family=M+PLUS+1p:wght@100;300;400;500;700;800;900&display=swap',
			},
		],
		scripts:
			import.meta.env.MODE === 'production'
				? [
						{
							children: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${VITE_GTM}');
              `,
						},
						{
							children: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                
                const savedConsent = localStorage.getItem('gdpr-consent');
                
                if (savedConsent) {
                  const consent = JSON.parse(savedConsent);
                  gtag('consent', 'default', {
                    'ad_storage': consent.marketing ? 'granted' : 'denied',
                    'ad_user_data': consent.marketing ? 'granted' : 'denied',
                    'ad_personalization': consent.marketing ? 'granted' : 'denied',
                    'analytics_storage': consent.analytics ? 'granted' : 'denied'
                  });
                } else {
                  gtag('consent', 'default', {
                    'ad_storage': 'denied',
                    'ad_user_data': 'denied',
                    'ad_personalization': 'denied',
                    'analytics_storage': 'denied'
                  });
                }
              `,
						},
						{
							defer: true,
							src: `https://www.googletagmanager.com/gtag/js?id=${VITE_GA_MEASUREMENT_ID}`,
						},
						{
							defer: true,
							children: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${VITE_GA_MEASUREMENT_ID}');
              `,
						},
					]
				: [],
	}),
	notFoundComponent: () => <NotFound />,
	shellComponent: RootComponent,
});

function RootComponent() {
	return (
		<RootDocument>
			<Outlet />
		</RootDocument>
	);
}

function RootDocument({ children }: { children: ReactNode }) {
	const { i18n } = useTranslation();

	const router = useRouter();

	const newYearEventsLastDate = dayjs('2026').add(7, 'day').format();
	const diff = dayjs().diff(newYearEventsLastDate, 'days');
	const isNewYearEventsOver = diff >= 0;

	const closeCart = cartStore(useShallow((state) => state.closeCart));

	useEffect(() => {
		const unsub = router.subscribe('onBeforeNavigate', () => {
			closeCart();
		});

		return () => unsub();
	}, [router]);

	return (
		<html lang={i18n.language} suppressHydrationWarning>
			<head>
				<HeadContent />
			</head>

			<body>
				<Header />

				<main className="flex flex-col gap-4 overflow-hidden min-h-[calc(100svh_-_110px_-_72px)] max-2xl:min-h-[calc(100svh_-_94px_-_72px)] z-10">
					{children}
				</main>

				<Footer />

				<Cart />
				<ServiceDateModal />

				<Toaster position="bottom-right" className="toaster" />
				<Scripts />
				<Messengers />
				<Devtools />
				<GDPRBanner />

				{!isNewYearEventsOver && (
					<Suspense>
						<div className="w-full h-full z-100 fixed top-0 left-0 pointer-events-none">
							<Snowfall snowflakeCount={100} speed={[0.5, 2.5]} wind={[-2, 4]} radius={[0.5, 2]} />
						</div>
					</Suspense>
				)}
			</body>
		</html>
	);
}
